import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { withStyles } from '@mui/styles';

const CustomAutocomplete = withStyles({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input':
      {
        padding: 0
      }
  }
})((props: any) => <Autocomplete {...props} />);

const SearchBoxWithChip = ({
  title = 'Search keyword or Brand',
  placeholder = 'Separated by enter',
  onKeypressSearch,
  onDeleteSearch,
  onClearSearch,
  initialValue = []
}: {
  title: string;
  placeholder: string;
  onKeypressSearch?: (chips: string[]) => void;
  onDeleteSearch?: (chip: string[]) => void;
  onClearSearch?: () => void;
  initialValue?: string[];
}) => {
  const [chips, setChips] = useState<string[]>(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Update chips when initialValue changes
  useEffect(() => {
    setChips(initialValue);
  }, [initialValue]);

  const handleInputChange = (event: any, value: string, reason: string) => {
    // Handle input change for autocomplete
    setInputValue(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      event.stopPropagation();
      // Create a chip for the entire input value when Enter is pressed
      const newChip = inputValue.trim();
      const updatedChips = [...chips, newChip];

      setChips(updatedChips);
      onKeypressSearch?.(updatedChips);
      // Clear the input
      setInputValue('');
    }
  };

  const handleDeleteChip = (chipToDelete: string) => {
    setChips((prevChips) => {
      const updatedChips = prevChips.filter((chip) => chip !== chipToDelete);
      onDeleteSearch?.(updatedChips);
      return updatedChips;
    });
  };

  const handleClearSearch = () => {
    setChips([]);
    onClearSearch?.();
  };

  return (
    <Box
      sx={{
        border: '1px solid #D9D9D9',
        backgroundColor: '#FFF',
        padding: '10px 16px 6px 16px',
        borderRadius: 1,
        minHeight: 100,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'text',
        '&:hover': {
          border: '1px solid #064E66',
          boxShadow: '0 0 0 1px #064E66'
        },
        '&:focus-within': {
          border: '1px solid #064E66',
          boxShadow: '0 0 0 1px #064E66'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          mb: 1
        }}
      >
        <SearchIcon
          sx={{
            color: '#009FE3',
            width: '18px',
            height: '18px',
            flexShrink: 0,
            display: isFocused ? 'block' : 'none',
            opacity: isFocused ? 1 : 0,
            transform: isFocused ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 0.2s ease-in-out',
            pointerEvents: isFocused ? 'auto' : 'none'
          }}
        />
        <Typography
          component="span"
          sx={{ color: '#4b4b4b', fontSize: '13px', fontWeight: 600 }}
        >
          {title}
        </Typography>
      </Box>
      <CustomAutocomplete
        multiple
        freeSolo
        value={chips}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={(event, newValue) => {
          if (Array.isArray(newValue)) {
            setChips(newValue);
            // Check if chips are being deleted (newValue has fewer items than current chips)
            if (newValue.length < chips.length) {
              // Use onDeleteSearch for immediate filter application when deleting
              onDeleteSearch?.(newValue);
            } else {
              // Use onKeypressSearch for adding new chips
              onKeypressSearch?.(newValue);
            }
          }
        }}
        options={[]}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={option}
              onDelete={() => handleDeleteChip(option)}
              size="small"
              sx={{
                backgroundColor: '#FFF',
                color: '#064E66',
                border: '1px solid #D9D9D9',
                fontSize: '13px',
                '& .MuiChip-deleteIcon': {
                  color: '#E35200'
                }
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            ref={inputRef}
            variant="standard"
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              '& .MuiInput-underline:before': {
                borderBottom: 'none'
              },
              '& .MuiInput-underline:after': {
                borderBottom: 'none'
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottom: 'none'
              },
              '& .MuiInput-underline.Mui-focused:after': {
                borderBottom: 'none'
              },
              '& .MuiInput-underline.Mui-focused:before': {
                borderBottom: 'none'
              },
              '& .MuiInputBase-input': {
                padding: 0,
                fontSize: '14px',
                '&::placeholder': {
                  color: '#9D9D9D',
                  opacity: 1
                }
              }
            }}
          />
        )}
        sx={{
          maxHeight: '40px',
          overflowY: 'scroll',
          '& .MuiAutocomplete-root': {
            minHeight: '40px',
            maxHeight: '40px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '4px',
              height: '4px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#064E66',
              borderRadius: '1px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(6, 78, 102, 0.5)'
            }
          },
          '& .MuiInput-underline:before': {
            borderBottom: 'none !important'
          },
          '& .MuiInput-underline:after': {
            borderBottom: 'none !important'
          },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 'none !important'
          },
          '& .MuiInput-underline.Mui-focused:after': {
            borderBottom: 'none !important'
          },
          '& .MuiInput-underline.Mui-focused:before': {
            borderBottom: 'none !important'
          }
        }}
      />
    </Box>
  );
};

export default SearchBoxWithChip;
